import {AuthGuard} from '@ccp/app/guards/auth.guard';
import {Debug} from '@ccp/app/debug';
import {LoginGuard} from '@ccp/app/guards/login.guard';
import {OptinGuard} from '@ccp/app/guards/optin.guard';
import {ProductGuard} from '@ccp/app/guards/product.guard';
import {RegistrationGuard} from '@ccp/app/guards/registration.guard';
import {RedirectFunction, Routes} from '@angular/router';
import {SimpleRegistrationGuard} from '@ccp/app/guards/simple-registration.guard';
import {Views} from '@ccp/config/views';
import {ProductRegisterGuard} from '@ccp/app/guards/product-register.guard';
import {NextgenCSActivateGuard} from '@ccp/app/guards/ngcs/nextgen-cs-guard.service';
import {TokenValidityResolver} from './resolvers/token-validity-resolver.services';
import {ProductResolver} from '@ccp/app/resolvers/product-resolver.service';
import {TokenValidityType} from '@ccp/struct/tokenValidity';
import {SimpleRegistrationResolver} from '@ccp/app/resolvers/simple-registration-resolver.services';
import {DigitalCertificateGuard} from '@ccp/app/guards/dc/digital-certificate.guard';
import {DigitalCertificateContextGuard} from '@ccp/app/guards/dc/digital-certificate-context.guard';
import {ScanGuard} from '@ccp/app/guards/scan.guard';
import {EnquirusActivatedGard} from './guards/enquirus-activated.guard';
import {EnquirusAccountCreationGuard} from './guards/enquirus-account-creation.guard';
import {EnquirusAddTimepieceResolver} from '@ccp/app/resolvers/enquirus-add-timepiece-resolver.service';
import {shareDataResolver} from '@ccp/app/resolvers/share-view-resolver.service';
import {inject} from '@angular/core';
import {FeatureToggleService} from '@ccp/services/toggle-features/feature-toggle.service';
import {ToggleFeatures} from '@ccp/enums/toggleFeatures.enum';

const defaultLandingRedirect: RedirectFunction = () => {
  const featureToggleService = inject(FeatureToggleService);
  const isLandingPageEnabled = !!featureToggleService.featureConfig$()?.getConfig(ToggleFeatures.landingPageByBrand);

  return isLandingPageEnabled ? Views.welcome.path : Views.productRegisterScanQrCode.path;
};

/**
 * Routing Dictionary.
 */
let routes: Routes = [
  {
    /**
     * This route is for old complete information component. It's the first one
     * checked but it should be at the same level of the others routes.
     * Didn't have time to move it because it's a completly new task of
     * checking, NRT, etc.
     */
    path: Views.completeAccount.path,
    loadComponent: () =>
      import('@ccp/views/users/registration/simple-registration/simple-registration.component').then((m) => m.SimpleRegistrationComponent),
    canActivate: [SimpleRegistrationGuard],
    resolve: {validityToken: TokenValidityResolver},
    data: {view: Views.simplifiedRegistration, tokenValidityType: TokenValidityType.VERIFY},
  },
  {
    path: '',
    redirectTo: defaultLandingRedirect,
    pathMatch: 'full',
  },
  {
    path: Views.welcome.path,
    loadComponent: () => import('@ccp/views/welcome/welcome.component').then((m) => m.WelcomeComponent),
    data: {view: Views.welcome},
  },
  {
    path: Views.maintenance.path,
    loadComponent: () => import('@ccp/views/maintenance/maintenance.component').then((m) => m.MaintenanceComponent),
    data: {view: Views.maintenance, withDelay: false},
  },
  {
    path: Views.maintenanceWhitDelay.path,
    loadComponent: () => import('@ccp/views/maintenance/maintenance.component').then((m) => m.MaintenanceComponent),
    data: {view: Views.maintenance, withDelay: true},
  },
  /**
   * User
   */
  {
    path: Views.userLogin.path,
    loadComponent: () => import('@ccp/views/users/login/login.component').then((m) => m.LoginComponent),
    canActivate: [LoginGuard],
    resolve: [ProductResolver],
    runGuardsAndResolvers: 'always',
    data: {view: Views.userLogin},
  },
  {
    path: Views.userRegister.path,
    loadComponent: () => import('@ccp/views/users/registration/registration.component').then((m) => m.RegistrationComponent),
    canActivate: [RegistrationGuard],
    runGuardsAndResolvers: 'always',
    data: {view: Views.userRegister},
  },
  {
    path: Views.noSellout.path,
    loadComponent: () => import('@ccp/views/products/register/no-sell-out/no-sell-out.component').then((m) => m.NoSellOutComponent),
    canActivate: [AuthGuard, ProductGuard],
    runGuardsAndResolvers: 'always',
    data: {view: Views.noSellout},
  },
  /**
   * DIGITAL CERTIFICATE
   */
  {
    path: 'certificate',
    canActivate: [DigitalCertificateGuard],
    loadChildren: () => import('@ccp/views/dc/dc-routing.routes'),
    data: {view: Views.digitalCertificate, preload: true},
  },
  {
    path: Views.certificateShare.path,
    loadComponent: () => import('@ccp/views/shared-view/shared-view.component').then((m) => m.SharedViewComponent),
    canActivate: [AuthGuard, DigitalCertificateGuard],
    resolve: {shareData: shareDataResolver},
    data: {view: Views.certificateShare},
    runGuardsAndResolvers: 'always',
  },
  {
    path: Views.certificateShareDisplay.path,
    loadComponent: () => import('@ccp/views/shared-view/shared-view.component').then((m) => m.SharedViewComponent),
    canActivate: [DigitalCertificateGuard],
    resolve: {shareData: shareDataResolver},
    runGuardsAndResolvers: 'always',
    data: {view: Views.certificateShareDisplay},
  },
  {
    path: Views.certificateShareSend.path,
    loadComponent: () => import('@ccp/views/dc/send-link/send-link.component').then((m) => m.SendLinkComponent),
    runGuardsAndResolvers: 'always',
    data: {view: Views.certificateShareSend},
  },
  {
    path: Views.certificateTransferSend.path,
    loadComponent: () => import('@ccp/views/dc/send-link/send-link.component').then((m) => m.SendLinkComponent),
    runGuardsAndResolvers: 'always',
    data: {view: Views.certificateTransferSend},
  },
  {
    path: Views.certificateTransferring.path,
    loadComponent: () => import('@ccp/views/transferring-view/transferring-view.component').then((m) => m.TransferringViewComponent),
    canActivate: [DigitalCertificateContextGuard],
    runGuardsAndResolvers: 'always',
    data: {view: Views.certificateTransferring},
  },
  {
    path: Views.simplifiedRegistration.path,
    loadComponent: () =>
      import('@ccp/views/users/registration/simple-registration/simple-registration.component').then((m) => m.SimpleRegistrationComponent),
    canActivate: [SimpleRegistrationGuard],
    resolve: {phoneNumber: SimpleRegistrationResolver},
    data: {view: Views.simplifiedRegistration},
  },
  {
    path: Views.myprofile.path,
    loadChildren: () => import('@ccp/views/users/my-account/my-account-routing.routes'),
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'always',
    data: {view: Views.profile, preload: true},
  },
  {
    path: Views.profile.path,
    loadChildren: () => import('@ccp/views/users/profile/profile-routing.routes'),
    runGuardsAndResolvers: 'always',
    data: {view: Views.profile, preload: true},
  },
  {
    path: Views.portfolio.path,
    loadComponent: () => import('@ccp/views/users/portfolio/portfolio.component').then((m) => m.PortfolioComponent),
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'always',
    data: {view: Views.portfolio},
  },
  {
    path: Views.portfolioFocus.path,
    loadComponent: () => import('@ccp/views/users/portfolio/portfolio.component').then((m) => m.PortfolioComponent),
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'always',
    data: {view: Views.portfolioFocus},
  },
  {
    path: Views.portfolioDetail.path,
    loadComponent: () => import('@ccp/views/users/portfolio/portfolio.component').then((m) => m.PortfolioComponent),
    canActivate: [AuthGuard],
    runGuardsAndResolvers: 'always',
    data: {view: Views.portfolioDetail, showDetail: true},
  },
  {
    path: Views.productRegister.path,
    canActivate: [ProductRegisterGuard],
    loadComponent: () =>
      import('@ccp/views/products/register/manual-register/manual-register.component').then((m) => m.ManualRegisterComponent),
    data: {state: 'manualRegister', view: Views.productRegister},
  },
  {
    path: Views.productRegisterScanQrCode.path,
    canActivate: [SimpleRegistrationGuard, ScanGuard],
    loadComponent: () => import('@ccp/views/products/register/scan-register/scan-register.component').then((m) => m.ScanRegisterComponent),
    data: {state: 'scanRegister', view: Views.productRegister, preload: true},
  },
  {
    path: Views.productVerification.path,
    loadComponent: () => import('@ccp/views/products/register/confirmation/confirmation.component').then((m) => m.ConfirmationComponent),
    runGuardsAndResolvers: 'always',
    data: {state: 'scanRegister', view: Views.productVerification},
  },
  {
    path: Views.productRequestStatus.path,
    loadComponent: () =>
      import('@ccp/views/products/register/request-status/request-status.component').then((m) => m.RequestStatusComponent),
    canActivate: [ProductGuard, AuthGuard],
    data: {view: Views.productRequestStatus},
    children: [
      {
        path: '',
        loadChildren: () => import('@ccp/views/products/register/request-status/request-status-routing.routes'),
      },
    ],
  },
  {
    path: Views.optin.path,
    loadComponent: () => import('@ccp/views/opt-in-page/opt-in-page.component').then((m) => m.OptInPageComponent),
    canActivate: [AuthGuard, OptinGuard],
    resolve: [ProductResolver],
    runGuardsAndResolvers: 'always',
    data: {view: Views.optin},
  },
  {
    path: Views.countrySelector.path,
    loadComponent: () =>
      import('@ccp/views/_shared/country-selection/country-selection.component').then((m) => m.CountrySelectionComponent),
    data: {view: Views.countrySelector},
  },
  {
    path: Views.notFound.path,
    loadComponent: () => import('@ccp/views/pagenotfound/page-not-found.component').then((m) => m.PageNotFoundComponent),
    data: {view: Views.notFound},
  },
  /**
   * ECS
   */
  {
    path: Views.myServices.path,
    redirectTo: Views.nextgenCs.path + '/my-services',
    pathMatch: 'full',
  },
  {
    path: Views.myServicesSearch.path,
    redirectTo: Views.nextgenCs.path + '/my-services',
    pathMatch: 'full',
  },
  /**
   * put all ecs modules path here to be easy to remove all ecs code after
   */
  {
    path: Views.costEstimate.path,
    redirectTo: Views.nextgenCs.path + '/my-services',
    pathMatch: 'full',
  },
  {
    path: Views.costEstimateDeclining.path,
    redirectTo: Views.nextgenCs.path + '/my-services',
    pathMatch: 'full',
  },
  {
    path: Views.costEstimateAccepted.path,
    redirectTo: Views.nextgenCs.path + '/my-services',
    pathMatch: 'full',
  },
  {
    path: Views.costEstimateDeclined.path,
    redirectTo: Views.nextgenCs.path + '/my-services',
    pathMatch: 'full',
  },
  {
    path: Views.costEstimateDirectAccess.path,
    redirectTo: Views.nextgenCs.path + '/my-services',
    pathMatch: 'full',
  },
  {
    path: Views.whatIsNext.path,
    redirectTo: Views.nextgenCs.path + '/my-services',
    pathMatch: 'full',
  },
  {
    path: Views.hydraLogin.path,
    loadComponent: () => import('@ccp/views/_hydra-widget/hydra-widget.component').then((m) => m.HydraWidgetComponent),
    data: {view: Views.hydraLogin},
  },
  {
    path: Views.nextgenCs.path,
    loadComponent: () => import('@ccp/views/nextgen-cs/nextgen-cs.component').then((m) => m.NextgenCsComponent),
    canActivate: [NextgenCSActivateGuard],
    data: {view: Views.nextgenCs},
    children: [
      {
        path: '',
        loadChildren: () => import('@ccp/views/nextgen-cs/nextgen-cs.routes'),
      },
    ],
  },
  {
    path: Views.actionPerformed.path,
    loadComponent: () => import('@ccp/views/action-performed/action-performed.component').then((m) => m.ActionPerformedComponent),
    data: {view: Views.actionPerformed},
  },
  {
    path: Views.enquirusConfirmation.path,
    loadComponent: () =>
      import('@ccp/app/views/enquirus/confirmation/enquirus-confirmation.component').then((m) => m.EnquirusConfirmationComponent),
    canActivate: [AuthGuard, EnquirusActivatedGard],
    resolve: {vaultRequestResult: EnquirusAddTimepieceResolver},
    data: {view: Views.enquirusConfirmation},
  },
  {
    path: Views.enquirusProtectyourTimepiece.path,
    loadComponent: () =>
      import('@ccp/views/enquirus/protect-your-timepiece/protect-your-timepiece.component').then((m) => m.ProtectYourTimepieceComponent),
    data: {view: Views.enquirusProtectyourTimepieceAccountCreation, preload: true},
  },
  {
    path: Views.enquirusProtectyourTimepieceAccountCreation.path,
    loadComponent: () =>
      import('@ccp/views/enquirus/protect-your-timepiece-account-creation/protect-your-timepiece-account-creation.component').then(
        (m) => m.ProtectYourTimepieceAccountCreationComponent,
      ),
    canActivate: [AuthGuard, EnquirusActivatedGard, EnquirusAccountCreationGuard],
    data: {view: Views.enquirusProtectyourTimepieceAccountCreation},
  },
  {
    path: Views.myEnquirusAccount.path,
    loadComponent: () =>
      import('@ccp/views/enquirus/my-enquirus-account/my-enquirus-account.component').then((m) => m.MyEnquirusAccountComponent),
    data: {view: Views.myEnquirusAccount},
    canActivate: [AuthGuard, EnquirusActivatedGard],
  },
  {
    path: Views.myEnquirusNotAvailable.path,
    loadComponent: () =>
      import('@ccp/views/enquirus/enquirus-not-available/enquirus-not-available.component').then((m) => m.EnquirusNotAvailableComponent),
    data: {view: Views.myEnquirusNotAvailable},
  },
  {
    path: Views.yourEnquirusVaultRequest.path,
    loadComponent: () => import('@ccp/views/enquirus/vault-request/vault-request.component').then((m) => m.VaultRequestComponent),
    data: {view: Views.yourEnquirusVaultRequest},
    canActivate: [AuthGuard, EnquirusActivatedGard],
  },
  {
    path: Views.retryPage.path,
    loadComponent: () => import('@ccp/views/error-retry-page/error-retry-page.component').then((m) => m.ErrorRetryPageComponent),
    data: {view: Views.retryPage},
  },
  {
    path: Views.RedirectToWechatMiniProgram.path,
    loadComponent: () =>
      import('@ccp/views/redirect-to-wechat-mini-program/redirect-to-wechat-mini-program.component').then(
        (m) => m.RedirectToWechatMiniProgramPage,
      ),
    data: {view: Views.RedirectToWechatMiniProgram},
  },
  {
    path: Views.default.path,
    redirectTo: 'page-not-found',
  },
];

routes = Debug.disableGuardsInRoutesIfWanted(routes);

export const RoutesDictionary: Routes = routes;
